import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  Button,
  LoadingSpinner,
} from "@/components/ui";
import type { ComponentProps, ReactNode } from "react";
import { useState } from "react";

interface ActionConfirmationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string;
  description?: ReactNode;
  buttonText?: string;
  afterActionFn?: () => unknown;
  cancelText?: string;
  onCancel?: () => void | Promise<void>;
  actionVariant?: ComponentProps<typeof Button>["variant"];
  /** When true, cancel/confirm are disabled and confirm shows a spinner (e.g. async mutation in progress). */
  isActionPending?: boolean;
  dismissOnOutsideClick?: boolean;
}

export function ActionConfirmationModal({
  open,
  setOpen,
  title,
  description,
  buttonText,
  afterActionFn,
  cancelText,
  onCancel,
  actionVariant = "destructive",
  isActionPending = false,
  dismissOnOutsideClick = false,
}: ActionConfirmationModalProps) {
  const [internalPending, setInternalPending] = useState(false);
  const pending = isActionPending || internalPending;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) {
          return;
        }
        setOpen(next);
      }}
    >
      <AlertDialogContent
        className="border-border bg-card text-card-foreground max-h-[70vh] overflow-y-auto shadow-lg"
        onOverlayClick={
          dismissOnOutsideClick
            ? () => {
                if (!pending) {
                  void onCancel?.();
                  setOpen(false);
                }
              }
            : undefined
        }
      >
        <AlertDialogTitle className="text-card-foreground line-clamp-5">
          {title ?? "Confirm action"}
        </AlertDialogTitle>
        <AlertDialogDescription className="text-muted-foreground line-clamp-15">
          {description || "This action cannot be undone."}
        </AlertDialogDescription>
        <div className="flex justify-end space-x-2">
          <AlertDialogCancel asChild>
            <Button
              variant="outline"
              className="mt-0"
              disabled={pending}
              onClick={() => {
                void onCancel?.();
              }}
            >
              {cancelText ?? "Cancel"}
            </Button>
          </AlertDialogCancel>
          <Button
            type="button"
            variant={actionVariant}
            disabled={pending}
            onClick={async () => {
              if (!afterActionFn || isActionPending) {
                return;
              }
              setInternalPending(true);
              try {
                const result = await afterActionFn();
                if (result !== false) setOpen(false);
              } finally {
                setInternalPending(false);
              }
            }}
          >
            {pending ? <LoadingSpinner className="h-4 w-4 shrink-0" /> : null}
            {buttonText ?? "Delete"}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
